import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckSquare } from "@fortawesome/pro-solid-svg-icons/faCheckSquare"
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown"
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight"
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes"
import { faCogs } from "@fortawesome/pro-solid-svg-icons/faCogs"
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle"
import { faRepeat } from "@fortawesome/pro-solid-svg-icons/faRepeat"
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { formatDateTime } from '../../../utils'

const LotteryConfigurationRow = ({
  configuration,
  gradeConfig = null,
  isMultiGrade = false,
  isExpanded = false,
  isSubRow = false,
  onToggleExpanded,
  onEdit,
  onDelete,
  onTest,
  onRerunTest,
  onRun,
}) => {
  const [isTesting, setIsTesting] = useState(false);
  const [isRunningLottery, setIsRunningLottery] = useState(false);

  const handleTest = () => {
    setIsTesting(true);
    onTest()
      .then(() => {
        setIsTesting(false);
      })
      .catch(() => {
        setIsTesting(false);
      });
  };

  // Helper function to get the lottery ID for a specific grade
  const getLotteryIdForGrade = (gradeId, isTest = true) => {
    const gradeIndex = configuration.grade_configurations.findIndex(gc => gc.grade_id === gradeId);
    if (gradeIndex >= 0) {
      if (isTest && configuration.lottery_test_ids && configuration.lottery_test_ids[gradeIndex]) {
        return configuration.lottery_test_ids[gradeIndex];
      } else if (!isTest && configuration.lottery_result_ids && configuration.lottery_result_ids[gradeIndex]) {
        return configuration.lottery_result_ids[gradeIndex];
      }
    }
    return null;
  };

  // Helper function to get the lottery status for a specific lottery grade configuration
  const getLotteryStatusForGrade = (gradeId, isTest = true) => {
    const lotteryId = getLotteryIdForGrade(gradeId, isTest);
    if (lotteryId && configuration.lottery_statuses && configuration.lottery_statuses[lotteryId]) {
      return configuration.lottery_statuses[lotteryId];
    }
    return 'pending'
  };

  // Helper function to check if any tests are currently pending
  const hasAnyTestsPending = () => {
    if (!configuration.has_test_results) {
      return false;
    }

    return configuration.grade_configurations.some(gc => {
      const testStatus = getLotteryStatusForGrade(gc.grade_id, true);
      return testStatus === 'pending';
    });
  };

  // Helper function to check if all tests have passed successfully
  const haveAllTestsPassed = () => {
    if (!configuration.has_test_results) {
      return false;
    }

    return configuration.grade_configurations.every(gc => {
      const testStatus = getLotteryStatusForGrade(gc.grade_id, true);
      return testStatus === 'finished';
    });
  };

  const getLotteryStatusBadge = (status) => {
    const statusConfig = {
      'pending': {
        icon: faCogs,
        className: 'text-warning',
        label: 'Pending'
      },
      'finished': {
        icon: faCheckSquare,
        className: 'text-success',
        label: 'Finished'
      },
      'failed': {
        icon: faTimes,
        className: 'text-danger',
        label: 'Failed'
      }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const textColor = status === 'failed' ? 'red' : 'black';

    return (
      <span className={`${config.className}`}>
        <FontAwesomeIcon icon={config.icon} className="me-1" />
        <span style={{ color: textColor }}>{config.label}</span>
      </span>
    );
  };

  const getConfigStatusBadge = (type) => {
    // If configuration is not complete, it shouldn't show any status
    if (!configuration.complete) {
      return null;
    }

    // For multi-grade configurations, check if any lotteries are still pending
    if (isMultiGrade && !isSubRow) {
      const hasResults = type === 'test' ? configuration.has_test_results : configuration.has_lottery_results;

      if (hasResults) {
        // Check if any grade has pending status
        const hasPendingGrades = configuration.grade_configurations.some(gc => {
          const gradeStatus = getLotteryStatusForGrade(gc.grade_id, type === 'test');
          return gradeStatus === 'pending';
        });

        if (hasPendingGrades) {
          return (
            <span className="text-warning">
              <FontAwesomeIcon icon={faCogs} className="me-1" />
              <span style={{ color: 'black' }}>{type === 'test' ? 'Test Running' : 'Lottery Running'}</span>
            </span>
          );
        } else {
          // Check if any grade has failed status
          const hasFailedGrades = configuration.grade_configurations.some(gc => {
            const gradeStatus = getLotteryStatusForGrade(gc.grade_id, type === 'test');
            return gradeStatus === 'failed';
          });

          const icon = hasFailedGrades ? faExclamationTriangle : faCheckSquare;
          const iconClass = hasFailedGrades ? 'text-warning' : 'text-success';

          return (
            <span className="">
              <FontAwesomeIcon icon={icon} className={`me-1 ${iconClass}`} />
              <span style={{ color: 'black' }}>{type === 'test' ? 'Tests Finished' : 'Lotteries Finished'}</span>
            </span>
          );
        }
      }
    }

    const hasResults = type === 'test' ? configuration.has_test_results : configuration.has_lottery_results;
    const displayStatus = hasResults ? 'completed' : 'ready';
    const displayLabel = hasResults ? `${type === 'test' ? 'Test' : 'Lottery'} Completed` : `Ready to ${type}`;

    const statusConfig = {
      'ready': {
        className: 'text-info',
        label: displayLabel
      },
      'completed': {
        className: 'text-success',
        label: displayLabel
      }
    };

    const config = statusConfig[displayStatus];

    return (
      <span className={`${config.className}`}>
        <span style={{ color: 'black' }}>{config.label}</span>
      </span>
    );
  };

  const getCombinedStatusBadge = () => {
    // If configuration is not complete, it shouldn't show any status
    if (!configuration.complete) {
      return null;
    }

    // If lottery has been run and finished
    if (configuration.has_lottery_results) {
      if (isMultiGrade && !isSubRow) {
        // Check if any lottery is still pending
        const hasPendingLotteries = configuration.grade_configurations.some(gc => {
          const lotteryStatus = getLotteryStatusForGrade(gc.grade_id, false);
          return lotteryStatus === 'pending';
        });

        if (hasPendingLotteries) {
          return (
            <span className="text-warning">
              <FontAwesomeIcon icon={faCogs} className="me-1" />
              <span style={{ color: 'black' }}>Lottery Running</span>
            </span>
          );
        } else {
          return (
            <span className="">
              <FontAwesomeIcon icon={faCheckSquare} className="me-1 text-success" />
              <span style={{ color: 'black' }}>Lotteries Finished</span>
            </span>
          );
        }
      } else if (!isMultiGrade) {
        const lotteryStatus = getLotteryStatusForGrade(configuration.grade_configurations[0].grade_id, false);
        if (lotteryStatus === 'pending') {
          return (
            <span className="text-warning">
              <FontAwesomeIcon icon={faCogs} className="me-1" />
              <span style={{ color: 'black' }}>Lottery Running</span>
            </span>
          );
        } else {
          return (
            <span className="">
              <FontAwesomeIcon icon={faCheckSquare} className="me-1 text-success" />
              <span style={{ color: 'black' }}>Lotteries Finished</span>
            </span>
          );
        }
      }
    }

    // If tests have been run
    if (configuration.has_test_results) {
      if (isMultiGrade && !isSubRow) {
        // Check if any test is still pending
        const hasPendingTests = configuration.grade_configurations.some(gc => {
          const testStatus = getLotteryStatusForGrade(gc.grade_id, true);
          return testStatus === 'pending';
        });

        if (hasPendingTests) {
          return (
            <span className="text-warning">
              <FontAwesomeIcon icon={faCogs} className="me-1" />
              <span style={{ color: 'black' }}>Test Running</span>
            </span>
          );
        } else {
          // Check if any test failed
          const hasFailedTests = configuration.grade_configurations.some(gc => {
            const testStatus = getLotteryStatusForGrade(gc.grade_id, true);
            return testStatus === 'failed';
          });

          if (hasFailedTests) {
            return (
              <span className="">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-warning" />
                <span style={{ color: 'black' }}>Tests Failed</span>
              </span>
            );
          } else {
            return (
              <span className="">
                <FontAwesomeIcon icon={faCheckSquare} className="me-1 text-success" />
                <span style={{ color: 'black' }}>Ready to run</span>
              </span>
            );
          }
        }
      } else if (!isMultiGrade) {
        const testStatus = getLotteryStatusForGrade(configuration.grade_configurations[0].grade_id, true);
        if (testStatus === 'pending') {
          return (
            <span className="text-warning">
              <FontAwesomeIcon icon={faCogs} className="me-1" />
              <span style={{ color: 'black' }}>Test Running</span>
            </span>
          );
        } else if (testStatus === 'failed') {
          return (
            <span className="">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-warning" />
              <span style={{ color: 'black' }}>Tests Failed</span>
            </span>
          );
        } else {
          return (
            <span className="">
              <FontAwesomeIcon icon={faCheckSquare} className="me-1 text-success" />
              <span style={{ color: 'black' }}>Ready to run</span>
            </span>
          );
        }
      }
    }

    // If no tests have been run yet
    return (
      <span className="text-info">
        <span style={{ color: 'black' }}>Ready to test</span>
      </span>
    );
  };

  const formatGrades = gradeConfigs => {
    if (!gradeConfigs || gradeConfigs.length === 0) return 'None';

    return gradeConfigs.map(gc => `${gc.grade_name}`).join(', ');
  };

  const formatShortenGrades = gradeConfigs => {
    if (!gradeConfigs || gradeConfigs.length === 0) return 'None'

    const fullGradeNames = gradeConfigs.map(gc => gc.grade_name).join(', ');

    if (fullGradeNames.length <= 50) {
      return fullGradeNames;
    } else {
      return 'Multiple';
    }
  }

  const getGradeDisplay = () => {
    if (isSubRow && gradeConfig) {
      return gradeConfig.grade_name;
    }

    if (isMultiGrade && !isSubRow) {
      return formatShortenGrades(configuration.grade_configurations);
    }

    return formatGrades(configuration.grade_configurations);
  };

  const renderGradeDisplayWithTooltip = () => {
    const gradeDisplay = getGradeDisplay();

    // For multi-grade configurations, show tooltip with full grade names
    if (isMultiGrade && !isSubRow && configuration.grade_configurations && configuration.grade_configurations.length > 1) {
      const fullGradeNames = formatGrades(configuration.grade_configurations);

      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`grade-tooltip-${configuration.id}`}>
              {fullGradeNames}
            </Tooltip>
          }
        >
          <span>{gradeDisplay}</span>
        </OverlayTrigger>
      );
    }

    // For single grade or sub-rows, just return the display without tooltip
    return gradeDisplay;
  };

  const getSizeDisplay = () => {
    if (isSubRow && gradeConfig) {
      return gradeConfig.accept_limit;
    }

    if (isMultiGrade && !isSubRow) {
      return 'Multiple';
    }

    return configuration.grade_configurations.reduce((total, gc) => total + (gc.accept_limit || 0), 0);
  };

  const canTest = configuration.complete && !configuration.has_lottery_results;
  const canRun = configuration.complete && !configuration.has_lottery_results && haveAllTestsPassed();
  const canDelete = !configuration.has_lottery_results; // Can delete until production lottery is run?
  const canRerunTest = configuration.has_test_results && !configuration.has_lottery_results;
  const isTestsPending = hasAnyTestsPending();
  const isTestingInProgress = isTesting || isTestsPending;

  return (
    <>
      <tr
        data-configuration-id={!isSubRow ? configuration.id : `${configuration.id}-${gradeConfig.grade_id}`}
        className={isSubRow ? 'sub-row' : ''}
      >
        <td>
          {!isSubRow && isMultiGrade && (
            <button
              className="expand-toggle"
              onClick={onToggleExpanded}
              title={isExpanded ? "Collapse grades" : "Expand grades"}
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronDown : faChevronRight}
                className="text-muted"
              />
            </button>
          )}
          {isSubRow && (<span className="ms-3 text-muted"></span>)}
        </td>
        <td>{configuration.apply_year}</td>
        <td>{renderGradeDisplayWithTooltip()}</td>
        <td>{getSizeDisplay()}</td>
        <td>{formatDateTime(configuration.created_at)}</td>
        <td>{formatDateTime(configuration.updated_at)}</td>
        <td>
          {!isSubRow && (
            <button
              className="btn btn-link btn-sm p-0 bold"
              onClick={() => onEdit(configuration)}
              title="Edit lottery configuration"
            >
              Settings
            </button>
          )}
        </td>
        {/* COMBINED STATUS */}
        <td>
          {!isSubRow && getCombinedStatusBadge()}
          {isSubRow && gradeConfig && (
            (() => {
              if (configuration.has_lottery_results) {
                return getLotteryStatusBadge(getLotteryStatusForGrade(gradeConfig.grade_id, false));
              } else if (configuration.has_test_results) {
                return getLotteryStatusBadge(getLotteryStatusForGrade(gradeConfig.grade_id, true));
              }
              return null;
            })()
          )}
        </td>
        <td>
          {!isSubRow && (
            <div>
              {/* Show Test button if configuration can be tested and doesn't have test results yet */}
              {canTest && !configuration.has_test_results && (
                <span className="btn-group btn-group-sm" style={{ width: '100%' }}>
                  <button
                    className="btn btn-outline-primary btn-sm bold"
                    onClick={handleTest}
                    disabled={isTestingInProgress || !configuration.complete}
                  >
                    {isTestingInProgress ? 'Testing...' : 'Test'}
                  </button>
                </span>
              )}

              {configuration.has_test_results && isTestingInProgress && (
                <span className="btn-group btn-group-sm" style={{ width: '100%' }}>
                  <button
                    className="btn btn-outline-primary btn-sm bold"
                    disabled={true}
                  >
                    Testing...
                  </button>
                </span>
              )}

              {/* For single grade configurations, show test results link when tests are done */}
              {!isMultiGrade && configuration.has_test_results && configuration.lottery_test_ids && configuration.lottery_test_ids.length > 0 && !isTestsPending && (
                (() => {
                  const gradeStatus = getLotteryStatusForGrade(configuration.grade_configurations[0].grade_id, true);
                  return gradeStatus !== 'pending' ? (
                    <a
                      href={`/setup/lotteries/${configuration.lottery_test_ids[0]}`}
                      className="btn btn-link btn-sm p-0"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Test Results
                    </a>
                  ) : null;
                })()
              )}

              {/* For multi-grade configurations, show "View Results" button that expands accordion */}
              {isMultiGrade && configuration.has_test_results && !isTestsPending && (
                <button
                  className="btn btn-link btn-sm p-0"
                  onClick={onToggleExpanded}
                  title="View test results for all grades"
                >
                  View Results
                </button>
              )}

              {canRerunTest && !isTestingInProgress && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`rerun-test-tooltip-${configuration.id}`}>
                      Re-run test
                    </Tooltip>
                  }
                >
                  <button
                    className="btn btn-link btn-sm p-0 float-end"
                    onClick={() => onRerunTest(configuration)}
                    title="Re-run test"
                  >
                    <FontAwesomeIcon icon={faRepeat} />
                  </button>
                </OverlayTrigger>
              )}
            </div>
          )}

          {/* For multi-grade configurations, show test results link on sub-rows */}
          {isSubRow && gradeConfig && configuration.has_test_results && (
            (() => {
              const lotteryId = getLotteryIdForGrade(gradeConfig.grade_id, true);
              const gradeStatus = getLotteryStatusForGrade(gradeConfig.grade_id, true);
              return lotteryId && gradeStatus !== 'pending' ? (
                <a
                  href={`/setup/lotteries/${lotteryId}`}
                  className="btn btn-link btn-sm p-0"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Test Results
                </a>
              ) : null;
            })()
          )}
        </td>
        <td>
          {!isSubRow && (
            <div className="btn-group btn-group-sm">
              {canRun && (
                <button
                  className="btn btn-primary btn-sm bold"
                  onClick={onRun}
                  disabled={isRunningLottery || !configuration.complete}
                >
                  {isRunningLottery ? 'Running...' : 'Run Lottery'}
                </button>
              )}

              {/* For single grade configurations, show lottery results link */}
              {!isMultiGrade && configuration.has_lottery_results && configuration.lottery_result_ids && configuration.lottery_result_ids.length > 0 && (
                (() => {
                  const gradeStatus = getLotteryStatusForGrade(configuration.grade_configurations[0].grade_id, false);
                  return gradeStatus !== 'pending' ? (
                    <a
                      href={`/setup/lotteries/${configuration.lottery_result_ids[0]}`}
                      className="btn btn-link btn-sm p-0"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Lottery Results
                    </a>
                  ) : null;
                })()
              )}

              {/* For multi-grade configurations, show "View Results" button that expands accordion */}
              {isMultiGrade && configuration.has_lottery_results && (
                <button
                  className="btn btn-link btn-sm p-0"
                  onClick={onToggleExpanded}
                  title="View lottery results for all grades"
                >
                  View Results
                </button>
              )}
            </div>
          )}

          {/* Sub-row: Show link to specific grade's lottery results only */}
          {isSubRow && gradeConfig && configuration.has_lottery_results && (
            <div className="btn-group btn-group-sm">
              {(() => {
                const lotteryId = getLotteryIdForGrade(gradeConfig.grade_id, false);
                const gradeStatus = getLotteryStatusForGrade(gradeConfig.grade_id, false);
                return lotteryId && gradeStatus !== 'pending' ? (
                  <a
                    href={`/setup/lotteries/${lotteryId}`}
                    className="btn btn-link btn-sm p-0"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Lottery Results
                  </a>
                ) : null;
              })()}
            </div>
          )}
        </td>
        <td>
          {!isSubRow && (
            <div className="btn-group btn-group-sm">
              {canDelete && (
                <button
                  className="btn btn-link btn-sm p-0 text-danger"
                  onClick={() => onDelete(configuration)}
                  disabled={isTestingInProgress || (isMultiGrade && configuration.has_lottery_results)}
                >
                  Delete
                </button>
              )}

              {/* For single grade configurations with lottery results, show presentation link */}
              {!isMultiGrade && configuration.has_lottery_results && configuration.lottery_result_ids && configuration.lottery_result_ids.length > 0 && (
                (() => {
                  const gradeStatus = getLotteryStatusForGrade(configuration.grade_configurations[0].grade_id, false);
                  return gradeStatus !== 'pending' ? (
                    <a
                      href={`/setup/lotteries/${configuration.lottery_result_ids[0]}?presentation=1`}
                      className="btn btn-link btn-sm p-0"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Presentation
                    </a>
                  ) : null;
                })()
              )}
            </div>
          )}

          {/* Sub-row: Show presentation link for specific grade's lottery results only */}
          {isSubRow && gradeConfig && configuration.has_lottery_results && (
            <div className="btn-group btn-group-sm">
              {(() => {
                const lotteryId = getLotteryIdForGrade(gradeConfig.grade_id, false);
                const gradeStatus = getLotteryStatusForGrade(gradeConfig.grade_id, false);
                return lotteryId && gradeStatus !== 'pending' ? (
                  <a
                    href={`/setup/lotteries/${lotteryId}?presentation=1`}
                    className="btn btn-link btn-sm p-0"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Presentation
                  </a>
                ) : null;
              })()}
            </div>
          )}
        </td>
      </tr>
    </>
  );
};

export default LotteryConfigurationRow;
